import { useState, useEffect } from "react";
import moment from "moment";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
// Constants - functions
import { WEB_SERVICE_MIMS, applications } from "../../config/constants";
import { filterDataByDate } from "../../config/functions";
import { getProtocolTitle } from "../../config/validateProtocols";
// Components
import {
  SoftwareButtonsMain,
  SoftwareButtonsDiv,
  SoftwareButton,
  SessionHeaderMainDiv,
  SessionHeaderDiv,
  SessionTitle,
  SessionHideDiv,
} from "./styles";
import Loader from "../loader";
import Axios from "../../config/axios";
import PatientNavigation from "../patientNavigation";
import Title from "../title";
import AdvancedMenu from "../advancedMenu";
import AdherenceWheels from "../adherenceWheels";
import ScorePlot from "../reports/sections/scorePlot";
import HitsAndErrorsPlot from "../reports/sections/hitsAndErrorsPlot";
import SessionDurationPlot from "../reports/sections/sessionDurationPlot";
import SessionData from "./sessionData";

const GlobalPatientAdherence = () => {
  const [isLoadingAdherence, setIsLoadingAdherence] = useState(true);
  const [isLoadingReports, setIsLoadingReports] = useState(true);
  const [currentSoftware, setCurrentSoftware] = useState("app");
  const [sessionTimeRange, setSessionTimeRange] = useState("WEEK");
  const [adherence, setAdherence] = useState({});
  const [patientApplications, setPatientApplications] = useState([]);
  // Recordings
  const [recordings, setRecordings] = useState([]);
  const [currentRecordings, setCurrentRecordings] = useState([]);

  const { patientId } = useParams();
  const [t] = useTranslation("global");

  // Fetch patient data
  useEffect(() => {
    Axios("get", `${WEB_SERVICE_MIMS}/patients/fetch/${patientId}`)
      .then(({ data }) => {
        if (data.SOFTWARE_ID.includes(applications.plus.id)) setCurrentSoftware("plus");
        setPatientApplications(data.SOFTWARE_ID);
      })
      .catch((error) => {
        console.log("Endpoint to get user data: ", error);
      });
  }, [patientId]);

  // Fetching adherence
  useEffect(() => {
    Axios("get", `${WEB_SERVICE_MIMS}/adherence/software/get/${patientId}/${currentSoftware}/${sessionTimeRange}`)
      .then(({ data }) => {
        setAdherence(data);
        setIsLoadingAdherence(false);
      })
      .catch((error) => {
        console.log("Get application adherence: ", error);
      });
  }, [patientId, currentSoftware, sessionTimeRange]);

  // Fetching Recordings
  useEffect(() => {
    Axios("get", `${WEB_SERVICE_MIMS}/reports/software/get/${patientId}/${currentSoftware}`)
      .then(({ data }) => {
        const fetchedRecordings = data.sort((a, b) => new Date(a.STARTING_DATE) - new Date(b.STARTING_DATE));
        setRecordings(fetchedRecordings);
        setIsLoadingReports(false);
      })
      .catch((error) => {
        console.log("Get protocol recordings: ", error);
      });

    return () => {
      setRecordings([]);
      setCurrentRecordings([]);
      setIsLoadingReports(true);
    };
  }, [patientId, currentSoftware]);

  // Update Recordings
  useEffect(() => {
    const currentRecordings = filterDataByDate(sessionTimeRange, recordings);
    setCurrentRecordings(currentRecordings);
  }, [recordings, sessionTimeRange]);

  const renderScorePlot = () => {
    const scoreRecordings = currentRecordings.filter((recording) => recording.hasOwnProperty("score"));
    if (scoreRecordings.length) return <ScorePlot software={currentSoftware} recordings={currentRecordings} />;
  };

  const renderHitsAndErrorsPlot = () => {
    const hitsAndErrorsRecordings = currentRecordings.filter(
      (recording) => recording.hasOwnProperty("totalSuccess") || recording.hasOwnProperty("totalErrors")
    );
    if (hitsAndErrorsRecordings.length) return <HitsAndErrorsPlot software={currentSoftware} recordings={currentRecordings} />;
  };

  const renderSessionDurationPlot = () => {
    const sessionDurationRecordings = currentRecordings.filter((recording) => recording.hasOwnProperty("sessionDuration(seconds)"));
    if (sessionDurationRecordings.length) return <SessionDurationPlot software={currentSoftware} recordings={currentRecordings} />;
  };

  const renderAdvancedMenu = () => {
    if (patientApplications.includes(applications.clinic.id)) return <AdvancedMenu />;
  };

  const renderButtonsBySoftware = () => {
    return (
      <SoftwareButtonsMain>
        <SoftwareButtonsDiv>
          {patientApplications.includes(applications.app.id) && (
            <SoftwareButton onClick={() => setCurrentSoftware("app")} active={currentSoftware === "app"}>
              {t("applications_app")}
            </SoftwareButton>
          )}
          {patientApplications.includes(applications.plus.id) && (
            <SoftwareButton onClick={() => setCurrentSoftware("plus")} active={currentSoftware === "plus"}>
              {t("applications_plus")}
            </SoftwareButton>
          )}
        </SoftwareButtonsDiv>
      </SoftwareButtonsMain>
    );
  };

  const renderHeaderTable = () => {
    if (Boolean(currentRecordings.length)) {
      return (
        <>
          <Title title={t("sessions").toUpperCase()} marginBottom="10px" />
          <SessionHeaderMainDiv>
            <SessionHeaderDiv>
              <SessionTitle width="100px" width910="75px">
                {t("session").toUpperCase()}
              </SessionTitle>
              <SessionTitle width="138px" width910="80px">
                {t("date").toUpperCase()}
              </SessionTitle>
              <SessionTitle width="108px" width910="80px">
                {t("time").toUpperCase()}
              </SessionTitle>
              <SessionHideDiv>
                <SessionTitle width="144px">{t("application").toUpperCase()}</SessionTitle>
                <SessionTitle width="106px">{t("activity").toUpperCase()}</SessionTitle>
                <SessionTitle width="90px">{t("score").toUpperCase()}</SessionTitle>
              </SessionHideDiv>
            </SessionHeaderDiv>
          </SessionHeaderMainDiv>
        </>
      );
    }
  };

  const renderTotalSessions = () => {
    const sortedSessions = [...currentRecordings].sort((a, b) => {
      return new Date(b.STARTING_DATE) - new Date(a.STARTING_DATE);
    });

    return sortedSessions.map((session, i) => {
      const sessionsIndex = sortedSessions.length - i;
      const sessionId = session.SESSION_ID;
      const date = moment(session.STARTING_DATE).format("DD/MM/YYYY");
      const hour = moment(session.STARTING_DATE).format("HH:mm:ss");
      const currentApplication = t(`applications_${currentSoftware}`);
      const protocolTitle = t(getProtocolTitle("protocols", session.PROTOCOL_ID));

      return (
        <SessionData
          key={sessionId}
          sessionNumber={sessionsIndex}
          date={date}
          hour={hour}
          software={currentApplication}
          protocolTitle={protocolTitle}
          score={session.score}
        />
      );
    });
  };

  return (
    <>
      {isLoadingAdherence || isLoadingReports ? (
        <Loader />
      ) : (
        <>
          <PatientNavigation patientId={patientId} />
          {renderAdvancedMenu()}
          {renderButtonsBySoftware()}
          <Title title={t("adherence").toUpperCase()} marginBottom="10px" />
          <AdherenceWheels adherenceObject={adherence} sessionTimeRange={sessionTimeRange} setSessionTimeRange={setSessionTimeRange} />
          {renderScorePlot()}
          {renderHitsAndErrorsPlot()}
          {renderSessionDurationPlot()}
          {renderHeaderTable()}
          {renderTotalSessions()}
        </>
      )}
    </>
  );
};

export default GlobalPatientAdherence;
